import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { Button, Card, CardBody, Input, Textarea, Divider, addToast } from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { Accordion, AccordionItem } from "@heroui/react";
import api from "../services/api";
import PartnersBanner from "../assets/images/partners-banner.png";


export const PartnersPage: React.FC = () => {
  const location = useLocation();
  const [activeSlide, setActiveSlide] = React.useState(0);
  const [priceFormSubmitted, setPriceFormSubmitted] = React.useState(false);
  const [proposalFormSubmitted, setProposalFormSubmitted] = React.useState(false);
  const [briefFormSubmitted, setBriefFormSubmitted] = React.useState(false);
  const [briefSubmitting, setBriefSubmitting] = React.useState(false);

  // Track which testimonial cards are expanded
  const [expandedTestimonials, setExpandedTestimonials] = React.useState<Record<number, boolean>>({});

  // Proposal (commercial request) form states
  const [proposalName, setProposalName] = React.useState("");
  const [proposalCompany, setProposalCompany] = React.useState("");
  const [proposalEmail, setProposalEmail] = React.useState("");
  const [proposalField, setProposalField] = React.useState("");
  const [proposalDesired, setProposalDesired] = React.useState("");
  const [proposalComment, setProposalComment] = React.useState("");
  const [proposalSubmitting, setProposalSubmitting] = React.useState(false);

  // Brief form fields
  const [briefName, setBriefName] = React.useState("");
  const [briefCompany, setBriefCompany] = React.useState("");
  const [briefEmail, setBriefEmail] = React.useState("");
  const [briefPhone, setBriefPhone] = React.useState("");
  const [briefProductType, setBriefProductType] = React.useState("");
  const [briefQuantity, setBriefQuantity] = React.useState("");
  const [briefDeadlines, setBriefDeadlines] = React.useState("");
  const [briefAdditional, setBriefAdditional] = React.useState("");

  // Updated gallery items with more examples
  const galleryItems = [
    {
      image: "https://img.heroui.chat/image/furniture?w=800&h=600&u=candle10",
      title: "Корпоративные подарки",
      description: "Свечи с логотипом вашей компании"
    },
    {
      image: "https://img.heroui.chat/image/furniture?w=800&h=600&u=diffuser5",
      title: "Брендированные диффузоры",
      description: "Элегантные диффузоры с вашим фирменным стилем"
    },
    {
      image: "https://img.heroui.chat/image/furniture?w=800&h=600&u=candle11",
      title: "Подарочные наборы",
      description: "Комплекты ароматической продукции в фирменной упаковке"
    },
    {
      image: "https://img.heroui.chat/image/furniture?w=800&h=600&u=diffuser6",
      title: "Эксклюзивные коллекции",
      description: "Уникальные ароматы, созданные специально для вашего бренда"
    },
    {
      image: "https://img.heroui.chat/image/furniture?w=800&h=600&u=gift1",
      title: "Корпоративные мероприятия",
      description: "Ароматизация пространства для ваших бизнес-событий"
    },
    {
      image: "https://img.heroui.chat/image/furniture?w=800&h=600&u=gift2",
      title: "Сезонные коллекции",
      description: "Специальные предложения к праздникам и значимым датам"
    }
  ];

  const heroStats = [
    { label: "Минимальный опт", value: "от 50 ед." },
    { label: "Срок производства", value: "2–3 недели" },
    { label: "Варианты кастома", value: "12+ решений" },
    { label: "Командный ответ", value: "≤ 24 часа" }
  ];

  const heroChecklist = [
    "Корпоративные подарки",
    "Опт для шоурумов и ритейла",
    "Мероприятия",
    "Фирменные ароматы"
  ];

  // Products for business categories
  const businessProducts = [
    {
      icon: "lucide:gift",
      title: "Корпоративные подарки",
      description: "Элегантные подарочные наборы с вашим логотипом для клиентов, партнеров и сотрудников. Индивидуальная комплектация и дизайн упаковки.",
      examples: ["Новогодние наборы", "Бизнес-подарки", "Подарки для VIP-клиентов"]
    },
    {
      icon: "lucide:building",
      title: "Ароматизация пространств",
      description: "Создание уникального аромата для вашего офиса, шоу-рума или торговой точки. Профессиональный подбор композиций для усиления бренд-идентичности.",
      examples: ["Офисы и коворкинги", "Бутики и шоу-румы", "Отели и рестораны"]
    },
    {
      icon: "lucide:package",
      title: "Брендированная продукция",
      description: "Эксклюзивная линейка ароматической продукции с вашим логотипом и в фирменном стиле. Возможность создания уникальной ароматической композиции.",
      examples: ["Свечи с логотипом", "Фирменные диффузоры", "Автопарфюмы"]
    },
    {
      icon: "lucide:rocket",
      title: "Маркетинговые активности",
      description: "Ароматическое сопровождение маркетинговых мероприятий, презентаций и промо-акций. Усиление эмоциональной связи с клиентами через ароматы.",
      examples: ["Презентации", "Выставки", "Специальные мероприятия"]
    }
  ];

  // Custom development features
  const customDevelopmentFeatures = [
    {
      icon: "lucide:palette",
      title: "Индивидуальный дизайн",
      description: "Разработка уникального дизайна продукта и упаковки, соответствующего фирменному стилю вашей компании"
    },
    {
      icon: "lucide:diamond",
      title: "Материалы премиум качества",
      description: "Использование только высококачественных ингредиентов и материалов для создания продукции класса люкс"
    },
    {
      icon: "lucide:camera",
      title: "Профессиональная фотосъёмка",
      description: "Организация фотосессии готовой продукции для ваших маркетинговых материалов и каталогов"
    },
    {
      icon: "lucide:truck",
      title: "Доставка разработанной продукции",
      description: "Организация надежной доставки до вашего офиса или напрямую получателям по всей России и за рубеж"
    },
    {
      icon: "lucide:globe",
      title: "Создание сайта под продукцию",
      description: "Разработка лендинга или полноценного интернет-магазина для продвижения созданной ароматической линейки"
    },
    {
      icon: "lucide:users",
      title: "Организация мастер-классов",
      description: "Проведение корпоративных мероприятий по созданию ароматических свечей для ваших сотрудников или клиентов"
    }
  ];

  // B2B advantages
  const b2bAdvantages = [
    {
      icon: "lucide:badge-percent",
      title: "Специальные цены",
      description: "Гибкая система скидок в зависимости от объема заказа и регулярности сотрудничества"
    },
    {
      icon: "lucide:calendar",
      title: "Приоритетное производство",
      description: "Сокращенные сроки изготовления для постоянных корпоративных клиентов"
    },
    {
      icon: "lucide:helping-hand",
      title: "Индивидуальный бренд-дизайн",
      description: "Разработка уникального оформления продукции под ваш фирменный стиль."
    },
    {
      icon: "lucide:boxes",
      title: "Ароматы по вашему запросу",
      description: "Создание эксклюзивного аромата для бренда, мероприятия или подарочной коллекции."
    }
  ];

  const benefits = [
    {
      icon: "lucide:leaf",
      title: "Натуральные материалы",
      description: "Только экологичные ингредиенты высочайшего качества"
    },
    {
      icon: "lucide:flask-conical",
      title: "Авторская ароматическая линейка",
      description: "Уникальные композиции, разработанные профессиональными парфюмерами"
    },
    {
      icon: "lucide:users",
      title: "Персональный подход",
      description: "Индивидуальное решение для каждого клиента"
    },
    {
      icon: "lucide:truck",
      title: "Быстрая доставка и кастомизация",
      description: "Оперативное выполнение заказов любой сложности"
    }
  ];

  const faqItems = [
    {
      key: "1",
      title: "Можно ли сделать брендирование свечей под наш логотип?",
      content: "Да, мы предлагаем полную кастомизацию продукции, включая нанесение вашего логотипа на свечи, диффузоры и упаковку. Наши дизайнеры помогут интегрировать ваш фирменный стиль в продукт наиболее элегантным образом."
    },
    {
      key: "2",
      title: "Какой минимальный заказ для опта?",
      content: "Минимальный оптовый заказ составляет 20 единиц продукции. Для корпоративных подарков с индивидуальным дизайном минимальный заказ — 50 единиц."
    },
    {
      key: "3",
      title: "Как быстро изготавливаются индивидуальные заказы?",
      content: "Стандартный срок изготовления индивидуального заказа составляет 2-3 недели с момента утверждения дизайна. При срочных заказах возможно ускорение производства до 7-10 рабочих дней (с дополнительной платой за срочность)."
    },
    {
      key: "4",
      title: "Предоставляете ли вы образцы продукции перед оформлением крупного заказа?",
      content: "Да, мы предлагаем заказать набор образцов с различными ароматами по специальной цене."
    },
    {
      key: "5",
      title: "Какие варианты упаковки доступны для корпоративных подарков?",
      content: "Мы предлагаем различные варианты упаковки: минималистичные картонные коробки, подарочные наборы в тубусах, а также индивидуальную разработку упаковки под ваш бренд."
    },
    {
      key: "6",
      title: "Можно ли создать уникальный аромат специально для нашей компании?",
      content: "Да, мы предлагаем услугу разработки эксклюзивного аромата для вашего бренда. Мы создадим несколько вариантов композиций, из которых вы сможете выбрать наиболее подходящий."
    },
    {
      key: "7",
      title: "Есть ли у вас доставка по всей России и международная доставка?",
      content: "Мы осуществляем доставку по всей России через надежные транспортные компании. Международная доставка также доступна, условия обсуждаются индивидуально в зависимости от страны и объема заказа."
    },
    {
      key: "8",
      title: "Какие способы оплаты доступны для корпоративных клиентов?",
      content: "Для юридических лиц доступна оплата по безналичному расчету. Мы работаем по предоплатой системе. Возможно заключение долгосрочных контрактов."
    }
  ];

  const nextSlide = () => {
    setActiveSlide((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
  };

  const handlePriceFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPriceFormSubmitted(true);
    setTimeout(() => setPriceFormSubmitted(false), 3000);
  };

  const handleProposalFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!proposalName.trim() || !proposalCompany.trim() || !proposalEmail.trim() || !proposalField.trim() || !proposalDesired.trim()) {
      addToast({ title: "Ошибка", description: "Пожалуйста, заполните обязательные поля.", color: "danger" });
      return;
    }

    setProposalSubmitting(true);
    try {
      const payload = {
        name: proposalName.trim(),
        company_name: proposalCompany.trim(),
        email: proposalEmail.trim(),
        field_of_activity: proposalField.trim(),
        desired_quantity_or_format: proposalDesired.trim(),
        comment: proposalComment.trim(),
      };

      await api.post('/partners/commercial-request/', payload, { headers: { 'Content-Type': 'application/json' } });

      setProposalFormSubmitted(true);
      addToast({ title: "Коммерческий запрос отправлен", description: "Спасибо! Мы свяжемся с вами в ближайшее время.", color: "success" });
    } catch (err: any) {
      console.error('Proposal send error', err);
      const msg = err?.response?.data?.message || err?.message || 'Неизвестная ошибка';
      addToast({ title: "Ошибка отправки запроса", description: msg, color: "danger" });
    } finally {
      setProposalSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Ароматическая продукция для вашего бизнеса — SAFONOVA</title>
        <meta name="description" content="Корпоративные решения SAFONOVA: брендирование, подарочные наборы и ароматизация пространств для бизнеса." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/partners'}`} />
        <meta property="og:title" content="Ароматическая продукция для вашего бизнеса — SAFONOVA" />
        <meta property="og:description" content="Корпоративные решения SAFONOVA: брендирование, подарочные наборы и ароматизация пространств для бизнеса." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/partners'}`} />
        <meta property="og:image" content={`${BASE_URL || ''}/og-default.png`} />
      </Helmet>
      {/* Hero Section with background image */}
      <section 
        className="relative overflow-hidden py-28 md:py-40"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.10)), url('${PartnersBanner}')`,
          backgroundPosition: "center 60%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat"
        }}
      >
        <div className="absolute inset-0 bg-black/35" aria-hidden="true" />
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary/30 via-transparent to-transparent opacity-60" aria-hidden="true" />
        <div className="relative z-10 container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-start text-white"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-white/70">B2B & оптовые решения</p>
              <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-light leading-tight">
                Партнерская ароматическая линейка
                <span className="font-semibold text-white"> для вашего бренда</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl">
                Разрабатываем корпоративные подарки, фирменные ароматы и оптовые партии с полным сопровождением: от брифа и образцов до брендированной упаковки и доставки.
              </p>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {heroChecklist.map((item) => (
                  <div key={item} className="flex items-center gap-3 px-4 bg-white/10 py-2 text-sm font-medium">
                    <span className="flex h-6 w-6 items-center justify-center bg-white/20">
                      <Icon icon="lucide:check" width={16} />
                    </span>
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Button 
                  color="primary" 
                  size="lg" 
                  className="font-medium text-base"
                  endContent={<Icon icon="lucide:arrow-down-right" width={20} />}
                  as="a"
                  href="#b2b-brief"
                >
                  Заполнить бриф
                </Button>
                <Button 
                  color="default" 
                  variant="light"
                  size="lg"
                  className="font-medium text-base text-white/80 hover:text-white"
                  endContent={<Icon icon="lucide:mail" width={20} />}
                  as="a"
                  href="#partnership-forms"
                >
                  Получить консультацию
                </Button>
              </div>
              <p className="mt-4 text-sm text-white/70">
                Или свяжитесь напрямую: +7 (995) 881-02-47 · <a href="mailto:info@safonova-brand.ru" className="text-foreground/70 hover:text-foreground relative group overflow-hidden">
                <span className="transition-colors duration-300 ease-out">info@safonova-brand.ru</span>
                <span className="absolute left-0 bottom-0 w-0 h-px bg-foreground transition-all duration-300 ease-in-out group-hover:w-full"></span></a>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section - Wider with more examples */}
      {/* <section className="py-24 bg-content2" id="gallery">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Наши <span className="font-medium">работы</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Примеры брендированной продукции, которую мы создали для наших партнеров
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            <div className="overflow-hidden">
              <motion.div
                key={activeSlide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <img 
                  src={galleryItems[activeSlide].image} 
                  alt={galleryItems[activeSlide].title} 
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/90 to-transparent p-8">
                  <h3 className="text-2xl font-medium mb-2">{galleryItems[activeSlide].title}</h3>
                  <p className="text-foreground/80">{galleryItems[activeSlide].description}</p>
                </div>
              </motion.div>
            </div>

            <Button
              isIconOnly
              variant="flat"
              color="primary"
              className="absolute top-1/2 left-4 -translate-y-1/2 bg-background/50 backdrop-blur-md"
              onPress={prevSlide}
            >
              <Icon icon="lucide:chevron-left" width={24} />
            </Button>

            <Button
              isIconOnly
              variant="flat"
              color="primary"
              className="absolute top-1/2 right-4 -translate-y-1/2 bg-background/50 backdrop-blur-md"
              onPress={nextSlide}
            >
              <Icon icon="lucide:chevron-right" width={24} />
            </Button>

            <div className="flex justify-center mt-6 gap-2">
              {galleryItems.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`w-3 h-3 ${
                    index === activeSlide ? "bg-primary" : "bg-foreground/20"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div> */}

          {/* Case Studies Grid */}
          {/* <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border border-divider bg-content1" disableRipple>
              <CardBody className="p-6">
                <h4 className="text-xl font-medium mb-3">Отель «Премиум»</h4>
                <div className="h-48 mb-4 overflow-hidden">
                  <img 
                    src="https://img.heroui.chat/image/furniture?w=600&h=400&u=hotel_case" 
                    alt="Кейс отеля" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-foreground/70 mb-3">
                  Разработка фирменного аромата для сети отелей, создание брендированных диффузоров для номеров и холлов.
                </p>
                <div className="bg-primary/10 p-3">
                  <p className="text-sm font-medium">Результат: рост узнаваемости бренда на 27%</p>
                </div>
              </CardBody>
            </Card>

            <Card className="border border-divider bg-content1" disableRipple>
              <CardBody className="p-6">
                <h4 className="text-xl font-medium mb-3">IT-компания «Нексус»</h4>
                <div className="h-48 mb-4 overflow-hidden">
                  <img 
                    src="https://img.heroui.chat/image/furniture?w=600&h=400&u=office_case" 
                    alt="Кейс IT-компании" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-foreground/70 mb-3">
                  Создание новогодних подарочных наборов для ключевых клиентов компании с индивидуальным дизайном упаковки.
                </p>
                <div className="bg-primary/10 p-3">
                  <p className="text-sm font-medium">Результат: рост лояльности клиентов на 35%</p>
                </div>
              </CardBody>
            </Card>

            <Card className="border border-divider bg-content1" disableRipple>
              <CardBody className="p-6">
                <h4 className="text-xl font-medium mb-3">Ресторан «Эстетика»</h4>
                <div className="h-48 mb-4 overflow-hidden">
                  <img 
                    src="https://img.heroui.chat/image/furniture?w=600&h=400&u=restaurant_case" 
                    alt="Кейс ресторана" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-foreground/70 mb-3">
                  Разработка авторских свечей с фирменным ароматом для создания уникальной атмосферы и продажи гостям.
                </p>
                <div className="bg-primary/10 p-3">
                  <p className="text-sm font-medium">Результат: дополнительный доход и усиление бренда</p>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section> */}

      {/* New section: Products for Your Business */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Продукция для <span className="font-medium">вашего бизнеса</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Широкий спектр решений для различных бизнес-задач и направлений
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {businessProducts.map((product, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <Card className="border border-divider bg-content2/50 h-full" disableRipple>
                  <CardBody className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="bg-primary/10 p-3">
                        <Icon icon={product.icon} className="text-primary" width={24} />
                      </div>
                      <h3 className="text-xl font-medium">{product.title}</h3>
                    </div>
                    <p className="text-foreground/80 mb-6">{product.description}</p>
                    
                    <div>
                      <p className="text-sm font-medium mb-2">Популярные запросы:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.examples.map((example, i) => (
                          <span key={i} className="bg-primary/10 text-sm px-3 py-1">
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* New section: Custom Development Solutions */}
      <section className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Разработаем продукцию <span className="font-medium">под ключ</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Полный цикл создания эксклюзивной ароматической продукции для вашего бизнеса
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {customDevelopmentFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <Card className="border border-divider h-full" disableRipple>
                  <CardBody className="p-6">
                    <div className="bg-primary/10 p-3 w-12 h-12 flex items-center justify-center mb-4">
                      <Icon icon={feature.icon} className="text-primary" width={24} />
                    </div>
                    <h3 className="text-lg font-medium mb-2">{feature.title}</h3>
                    <p className="text-foreground/70 text-sm">{feature.description}</p>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* New section: B2B Advantages */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Преимущества для <span className="font-medium">B2B-клиентов</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Особые условия сотрудничества для корпоративных партнеров
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {b2bAdvantages.map((advantage, index) => (
              <Card key={index} className="border border-divider bg-content2/50 h-full" disableRipple>
                <CardBody className="p-6">
                  <div className="bg-primary/10 p-3 w-12 h-12 flex items-center justify-center mb-4">
                    <Icon icon={advantage.icon} className="text-primary" width={24} />
                  </div>
                  <h3 className="text-lg font-medium mb-2">{advantage.title}</h3>
                  <p className="text-foreground/70 text-sm">{advantage.description}</p>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Brief Form Section */}
      <section id="b2b-brief" className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border border-primary" disableRipple>
              <CardBody className="p-8 md:p-12">
                <h3 className="text-3xl md:text-4xl font-light mb-6 text-center">Заполните <span className="font-medium">бриф</span></h3>
                <p className="text-foreground/70 text-center mb-8 max-w-2xl mx-auto">
                  Расскажите о вашем проекте, и мы свяжемся с вами для обсуждения деталей
                </p>

                <div className="space-y-6">
                  {briefFormSubmitted ? (
                    <div className="text-center py-12">
                      <h4 className="text-xl font-medium">Спасибо за ваше обращение.</h4>
                      <p className="text-foreground/70 mt-4">Мы обязательно с вами свяжемся в близжайшее время.</p>
                    </div>
                  ) : (
                    <form className="space-y-6" onSubmit={async (e) => {
                      e.preventDefault();
                      // simple validation
                      if (!briefName.trim() || !briefCompany.trim() || !briefEmail.trim() || !briefPhone.trim() || !briefProductType.trim() || !briefQuantity.trim()) {
                        addToast({ title: "Ошибка", description: "Пожалуйста, заполните обязательные поля.", color: "danger" });
                        return;
                      }

                      setBriefSubmitting(true);
                      try {
                        const payload = {
                          name: briefName.trim(),
                          company_name: briefCompany.trim(),
                          email: briefEmail.trim(),
                          phone: briefPhone.trim(),
                          product_type: briefProductType.trim(),
                          quantity: briefQuantity.trim(),
                          deadlines: briefDeadlines.trim(),
                          additional_info: briefAdditional.trim(),
                        };

                        await api.post('/partners/brief/', payload, { headers: { 'Content-Type': 'application/json' } });

                        // Replace form with thank-you message
                        setBriefFormSubmitted(true);
                        addToast({ title: "Бриф отправлен", description: "Спасибо! Мы свяжемся с вами в ближайшее время.", color: "success" });
                      } catch (err: any) {
                        console.error('Brief send error', err);
                        const msg = err?.response?.data?.message || err?.message || 'Неизвестная ошибка';
                        addToast({ title: "Ошибка отправки брифа", description: msg, color: "danger" });
                      } finally {
                        setBriefSubmitting(false);
                      }
                    }}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          type="text"
                          label="Имя"
                          placeholder="Введите ваше имя"
                          variant="bordered"
                          isRequired
                          value={briefName}
                          onValueChange={(v: string) => setBriefName(v)}
                        />
                        <Input
                          type="text"
                          label="Название компании"
                          placeholder="Введите название компании"
                          variant="bordered"
                          isRequired
                          value={briefCompany}
                          onValueChange={(v: string) => setBriefCompany(v)}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input
                          type="email"
                          label="Email"
                          placeholder="Введите ваш email"
                          variant="bordered"
                          isRequired
                          value={briefEmail}
                          onValueChange={(v: string) => setBriefEmail(v)}
                        />
                        <Input
                          type="tel"
                          label="Телефон"
                          placeholder="Введите ваш телефон"
                          variant="bordered"
                          isRequired
                          value={briefPhone}
                          onValueChange={(v: string) => setBriefPhone(v)}
                        />
                      </div>

                      <div>
                        <Input
                          type="text"
                          label="Тип продукции"
                          placeholder="Какая ароматическая продукция вас интересует"
                          variant="bordered"
                          isRequired
                          value={briefProductType}
                          onValueChange={(v: string) => setBriefProductType(v)}
                        />
                      </div>

                      <div>
                        <Input
                          type="text"
                          label="Количество"
                          placeholder="Примерный объем заказа"
                          variant="bordered"
                          isRequired
                          value={briefQuantity}
                          onValueChange={(v: string) => setBriefQuantity(v)}
                        />
                      </div>

                      <div>
                        <Input
                          type="text"
                          label="Сроки"
                          placeholder="К какому сроку необходима продукция"
                          variant="bordered"
                          value={briefDeadlines}
                          onValueChange={(v: string) => setBriefDeadlines(v)}
                        />
                      </div>

                      <div>
                        <Textarea
                          label="Дополнительная информация"
                          placeholder="Особенности вашего бренда, пожелания по ароматам, дизайну и т.д."
                          variant="bordered"
                          minRows={4}
                          value={briefAdditional}
                          onValueChange={(v: string) => setBriefAdditional(v)}
                        />
                      </div>

                      <div className="text-center">
                        <Button 
                          type="submit"
                          color="primary" 
                          size="lg"
                          className="px-12"
                          isLoading={briefSubmitting}
                        >
                          Отправить бриф
                        </Button>
                      </div>
                      </form>
                  )}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Наши <span className="font-medium">преимущества</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Почему ведущие бренды выбирают сотрудничество с нами
            </p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.5,
              staggerChildren: 0.1
            }}
            viewport={{ once: true, margin: "-100px" }}
          >
            {benefits.map((benefit, index) => (
              <Card key={index} className="border border-divider bg-content2/50 h-full" disableRipple>
                <CardBody className="p-6">
                  <div className="bg-primary/10 p-3 w-12 h-12 flex items-center justify-center mb-4">
                    <Icon icon={benefit.icon} className="text-primary" width={24} />
                  </div>
                  <h3 className="text-lg font-medium mb-2">{benefit.title}</h3>
                  <p className="text-foreground/70 text-sm">{benefit.description}</p>
                </CardBody>
              </Card>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Часто задаваемые <span className="font-medium">вопросы</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Ответы на популярные вопросы о корпоративном сотрудничестве и оптовых заказах
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion variant="bordered" className="bg-content2/50">
              {faqItems.map((item) => (
                <AccordionItem key={item.key} title={item.title} aria-label={item.title}>
                  <p className="text-foreground/80">{item.content}</p>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section id="partnership-forms" className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Начните <span className="font-medium">сотрудничество</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Оставьте заявку, и мы свяжемся с вами для обсуждения деталей
            </p>
          </div>

            {/* Proposal Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Card className="border border-primary bg-content2 h-full" disableRipple>
                <CardBody className="p-8">
                  <h3 className="text-2xl font-light mb-6">Запросить <span className="font-medium">коммерческое предложение</span></h3>
                  
                  {proposalFormSubmitted ? (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-12"
                    >
                      <div className="bg-primary/10 p-4 w-16 h-16 flex items-center justify-center mx-auto mb-6">
                        <Icon icon="lucide:check" className="text-primary" width={32} />
                      </div>
                      <h4 className="text-xl font-medium mb-2">Спасибо за ваше обращение!</h4>
                      <p className="text-foreground/70">
                        Мы обязательно свяжемся с вами в близжайшее время.
                      </p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleProposalFormSubmit} className="space-y-6">
                      <Input
                        type="text"
                        label="Имя"
                        placeholder="Введите ваше имя"
                        variant="bordered"
                        isRequired
                        value={proposalName}
                        onValueChange={(v: string) => setProposalName(v)}
                      />
                      <Input
                        type="text"
                        label="Название компании"
                        placeholder="Введите название компании"
                        variant="bordered"
                        isRequired
                        value={proposalCompany}
                        onValueChange={(v: string) => setProposalCompany(v)}
                      />
                      <Input
                        type="email"
                        label="Email"
                        placeholder="Введите ваш email"
                        variant="bordered"
                        isRequired
                        value={proposalEmail}
                        onValueChange={(v: string) => setProposalEmail(v)}
                      />
                      <Input
                        type="text"
                        label="Сфера деятельности"
                        placeholder="Укажите сферу деятельности"
                        variant="bordered"
                        isRequired
                        value={proposalField}
                        onValueChange={(v: string) => setProposalField(v)}
                      />
                      <Input
                        type="text"
                        label="Желаемое количество или формат сотрудничества"
                        placeholder="Опишите ваши потребности"
                        variant="bordered"
                        isRequired
                        value={proposalDesired}
                        onValueChange={(v: string) => setProposalDesired(v)}
                      />
                      <Textarea
                        label="Комментарий"
                        placeholder="Дополнительная информация о вашем запросе"
                        variant="bordered"
                        value={proposalComment}
                        onValueChange={(v: string) => setProposalComment(v)}
                      />
                      <Button 
                        type="submit"
                        color="primary" 
                        size="lg"
                        className="w-full"
                        isLoading={proposalSubmitting}
                      >
                        Получить предложение
                      </Button>
                    </form>
                  )}
                </CardBody>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Отзывы <span className="font-medium">партнёров</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Что говорят о нас компании, которые уже сотрудничают с нами
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border border-divider bg-content2/50" disableRipple>
              <CardBody className="p-8">
                <div className="flex items-center mb-6">
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                </div>
                <div className="relative">
                  <p className={`text-foreground/80 mb-6 italic ${expandedTestimonials[0] ? '' : 'line-clamp-6'}`}>
                    "Благодарю вас за партнерство с моей конференцией ХАКАТОН’S. Предоставили необходимое количество товаров, в коммуникации все было четко и легко. Самое главное это сам продукт - упаковка, запах, качество свечей просто вау! все в восторге!"
                  </p>
                </div>
                <Divider className="mb-6" />
                <div className="flex items-center">
                  <div>
                    <h4 className="font-medium">Софи Гапенко</h4>
                    <p className="text-sm text-foreground/60">Организатор конференции ХАКАТОН’S</p>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card className="border border-divider bg-content2/50" disableRipple>
              <CardBody className="p-8">
                <div className="flex items-center mb-6">
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                </div>
                <div className="relative">
                  <p className={`text-foreground/80 mb-6 italic ${expandedTestimonials[1] ? '' : 'line-clamp-6'}`}>
                    "Мы благодaрим вас за партнёрство на конференции «Сияй»! Мы знаем, сколько знаний, любви и сил вы вложили в свой бренд. И как по-настоящему гордитесь тем, что сделали своими руками. Это чувствуется в каждой детали — от аромадиффузоров до массажных свечей. Вы уже сияете... И готовы светить еще ярче! Рады быть рядом с вами на этом пути! С любовью и уважением, команда конференции «Сияй»"
                  </p>
                  <button
                    type="button"
                    onClick={() => setExpandedTestimonials(prev => ({ ...prev, 1: !prev[1] }))}
                    className={`${expandedTestimonials[1] ? 'inline-block mt-2' : 'absolute right-0 bottom-0'} text-primary underline text-sm bg-background px-1 z-10 mb-1`}
                    aria-expanded={!!expandedTestimonials[1]}
                  >
                    {expandedTestimonials[1] ? 'Свернуть' : 'Показать полностью'}
                  </button>
                </div>
                <Divider className="mb-6" />
                <div className="flex items-center">
                  <div>
                    <h4 className="font-medium">Ольга Атабаева</h4>
                    <p className="text-sm text-foreground/60">Организатор конференции «Сияй»</p>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card className="border border-divider bg-content2/50" disableRipple>
              <CardBody className="p-8">
                <div className="flex items-center mb-6">
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                  <Icon icon="lucide:star" className="text-primary" width={20} />
                </div>
                <div className="relative">
                  <p className={`text-foreground/80 mb-6 italic ${expandedTestimonials[2] ? '' : 'line-clamp-6'}`}>
                    "Это был полный восторг! Анна подготовила для нашей бизнес-вечеринки массажные свечи в подарок каждой участнице и они произвели фурор! Хочется выделить ответственный подход Анны, что все заранее было спланировано, вовремя подготовлено на площадке и упаковано в отдельную подарочную коробку. Ароматы просто божественные! Наши девушки получили огромное удовольствие и делились самыми восторженными отзывами. Мы, организаторы, сами опробовали массажные свечи и скажем честно… такого мы никогда не пробовали. Эффект как от самого дорого элитного масла! Кожа после была в восторге!"
                  </p>
                  <button
                    type="button"
                    onClick={() => setExpandedTestimonials(prev => ({ ...prev, 2: !prev[2] }))}
                    className={`${expandedTestimonials[2] ? 'inline-block mt-2' : 'absolute right-0 bottom-0'} text-primary underline text-sm bg-background px-1 z-10 mb-1`}
                    aria-expanded={!!expandedTestimonials[2]}
                  >
                    {expandedTestimonials[2] ? 'Свернуть' : 'Показать полностью'}
                  </button>
                </div>
                <Divider className="mb-6" />
                <div className="flex items-center">
                  <div>
                    <h4 className="font-medium">Александра Куприянчук</h4>
                    <p className="text-sm text-foreground/60">Организатор мероприятия</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <div className="bg-primary/10 p-8 md:p-12 lg:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-6">Готовы создать <span className="font-medium">уникальный аромат</span> для вашего бренда?</h2>
            <p className="text-foreground/80 mb-8 max-w-2xl mx-auto">
              Свяжитесь с нами сегодня, чтобы обсудить возможности сотрудничества и получить индивидуальное предложение для вашего бизнеса.
            </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                as="a"
                href="tel:+79958810247"
                aria-label="Позвонить +7 (995) 881-02-47"
                color="primary" 
                size="lg"
                className="font-medium"
                endContent={<Icon icon="lucide:phone" width={18} />}
              >
                Позвонить нам
              </Button>
              <Button 
                as="a"
                href="mailto:info@safonova-brand.ru"
                aria-label="Написать на info@safonova-brand.ru"
                color="default" 
                variant="bordered" 
                size="lg"
                className="font-medium"
                endContent={<Icon icon="lucide:mail" width={18} />}
              >
                Написать на email
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};